import { ZoomStage, type ZoomTarget } from "../../camera/ZoomStage";
import { layoutKey, usePresenter } from "../context";
import { Footnotes, Sup } from "../Footnotes";
import { detailNodes, type FlowBlock } from "../model";
import { SlideHeading } from "../SlideHeading";
import { CodeBar } from "./CodeBar";
import { FlowDiagram, nodeCentre, placeFlow } from "./FlowDiagram";

/** Stage geometry from the prototype (Main.dc.html, "The agent loop"). */
const OVERVIEW_CENTRE_Y = 406;
const OVERVIEW_MAX_H = 360;
const DETAIL_LEFT = 198;
const DETAIL_WIDTH = 884;
const DETAIL_CENTRE_Y = 416;
const DETAIL_MAX_H = 190;

export function FlowSlide({ block, slideIndex, stop }: { block: FlowBlock; slideIndex: number; stop: number }) {
  const { layouts, goto } = usePresenter();
  const layout = layouts.get(layoutKey(block.id));
  if (!layout) return null;
  const at = placeFlow(layout, OVERVIEW_CENTRE_Y, OVERVIEW_MAX_H);
  const details = detailNodes(block);
  const focused = stop > 0 ? details[stop - 1] : undefined;
  // The last stop is the overview again, after every zoom: nothing left to point at.
  const back = stop > details.length;
  const hotId = back ? undefined : (focused ?? details[0])?.id;
  const target: ZoomTarget | null = focused ? { key: focused.id, origin: nodeCentre(layout, at, focused.id) } : null;
  const zoomTo = (id: string) => goto({ slideIndex, stop: details.findIndex((n) => n.id === id) + 1 });

  const overview = (
    <div className="slide">
      <SlideHeading
        blockId={block.id}
        title={block.title}
        lead={
          block.lead && (
            <>
              {block.lead}
              <Sup refs={block.refs} />
            </>
          )
        }
      />
      <FlowDiagram
        layout={layout}
        nodes={block.nodes}
        at={at}
        size="top"
        hotId={hotId}
        hint={!focused && !back}
        zoomable={new Set(details.map((n) => n.id))}
        onZoom={zoomTo}
      />
      <Footnotes refs={block.refs} />
    </div>
  );

  return (
    <ZoomStage
      target={target}
      overview={overview}
      detail={(nodeId) => <FlowDetail block={block} nodeId={nodeId} onZoomOut={() => goto({ slideIndex, stop: 0 })} />}
    />
  );
}

function FlowDetail({ block, nodeId, onZoomOut }: { block: FlowBlock; nodeId: string; onZoomOut: () => void }) {
  const { layouts } = usePresenter();
  const node = block.nodes.find((n) => n.id === nodeId);
  const detail = node?.detail;
  if (!node || !detail) return null;
  const sub = detail.flow;
  const subLayout = sub ? layouts.get(layoutKey(block.id, node.id)) : undefined;

  return (
    <div className="slide">
      <nav className="abs crumbs" style={{ left: DETAIL_LEFT, top: 72 }} aria-label="Breadcrumb">
        <button type="button" className="crumb-back" onClick={onZoomOut}>
          {block.title}
        </button>
        <span aria-hidden="true">›</span>
        <span className="crumb-node">{node.label}</span>
      </nav>
      <h2 className="abs zoomed-title" style={{ left: DETAIL_LEFT, top: 104, width: DETAIL_WIDTH, margin: 0 }}>
        {detail.title}
      </h2>
      {detail.lead && (
        <div className="abs slide-lead" style={{ left: DETAIL_LEFT, width: 780 }}>
          {detail.lead}
          <Sup refs={detail.refs} />
        </div>
      )}
      {sub && subLayout && (
        <FlowDiagram
          layout={subLayout}
          nodes={sub.nodes}
          at={placeFlow(subLayout, DETAIL_CENTRE_Y, DETAIL_MAX_H)}
          size="sub"
          hotId={sub.emphasis}
        />
      )}
      {detail.code && <CodeBar code={detail.code} left={DETAIL_LEFT} top={536} width={DETAIL_WIDTH} />}
      <Footnotes refs={detail.refs} left={DETAIL_LEFT} />
    </div>
  );
}
